import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Box, Stack} from 'views/components';

import {PlaybookDeleteDialog} from './components';
import PlaybooksList from './playbook_view/PlaybooksList';
import PlaybooksView from './playbook_view/PlaybooksView';
import PlaybookNew from './playbook_new/PlaybookNew';

import backend from 'utils/backend';

const PlaybooksScene = (props) => {
  const {
    playbooks,
    playbooksAdd,
    playbooksLoadData,
    playbooksRemove,
    statusProgressWrap: progressWrap,
  } = props;
  const playbookList = Array.isArray(playbooks) ? playbooks : [];

  const [activeNew, setActiveNew] = React.useState(false);
  const [editingPlaybook, setEditingPlaybook] = React.useState(null);
  const [selectedPlaybookRn, setSelectedPlaybookRn] = React.useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);

  const toStoredPlaybook = React.useCallback((playbookResponse) => {
    if (playbookResponse?.playbook) {
      return {
        ...playbookResponse.playbook,
        playbookRn: playbookResponse.playbookRn,
      };
    }

    return playbookResponse;
  }, []);

  const areStepsEqual = React.useCallback((leftStep, rightStep) => {
    const leftOwnerRns = [...(leftStep.ownerRns || [])].sort();
    const rightOwnerRns = [...(rightStep.ownerRns || [])].sort();

    return leftStep.prompt === rightStep.prompt &&
      leftStep.day === rightStep.day &&
      JSON.stringify(leftOwnerRns) === JSON.stringify(rightOwnerRns);
  }, []);

  const {groupOptions, groupedPlaybooks} = React.useMemo(() => {
    const uniqueGroups = new Set();
    const groupedPlaybooksMap = new Map();

    playbookList.forEach((playbook) => {
      if (playbook.groupName) {
        uniqueGroups.add(playbook.groupName);
      }

      const groupName = playbook.groupName || 'Ungrouped';
      const existingGroup = groupedPlaybooksMap.get(groupName);

      if (existingGroup) {
        existingGroup.push(playbook);
        return;
      }

      groupedPlaybooksMap.set(groupName, [playbook]);
    });

    return {
      groupOptions: Array.from(uniqueGroups),
      groupedPlaybooks: Array.from(groupedPlaybooksMap, ([groupName, groupedGroupPlaybooks]) => ({
        groupName,
        playbooks: groupedGroupPlaybooks,
      })),
    };
  }, [playbookList]);

  const selectedPlaybook = React.useMemo(() => {
    return playbookList.find((playbook) => playbook.playbookRn === selectedPlaybookRn) || null;
  }, [playbookList, selectedPlaybookRn]);

  React.useEffect(() => {
    progressWrap(async () => {
      const loadedPlaybooks = await backend.playbook.list();
      const playbookItems = loadedPlaybooks.list;

      playbooksLoadData(playbookItems);
    });
  }, [playbooksLoadData, progressWrap]);

  const openNew = React.useCallback(() => {
    setEditingPlaybook(null);
    setActiveNew(true);
  }, []);

  const closeNew = React.useCallback(() => {
    setActiveNew(false);
    setEditingPlaybook(null);
  }, []);

  const handleSelectPlaybook = React.useCallback((playbook) => {
    setSelectedPlaybookRn(playbook.playbookRn);
  }, []);

  const handleEditPlaybook = React.useCallback(() => {
    setEditingPlaybook(selectedPlaybook);
    setActiveNew(true);
  }, [selectedPlaybook]);

  const handleDeletePlaybook = React.useCallback(async () => {
    if (!selectedPlaybook?.playbookRn) {
      setDeleteDialogOpen(false);
      return;
    }

    await progressWrap(async () => {
      await backend.playbook.delete(selectedPlaybook.playbookRn);
      playbooksRemove(selectedPlaybook.playbookRn);
      setDeleteDialogOpen(false);
    });
  }, [playbooksRemove, progressWrap, selectedPlaybook]);

  const handleCreatePlaybook = React.useCallback(async (playbookPayload, existingPlaybookRn) => {
    await progressWrap(async () => {
      let playbookResponse = null;

      if (!existingPlaybookRn) {
        playbookResponse = await backend.playbook.create(playbookPayload);
      } else {
        const currentPlaybook = playbookList.find((currentItem) => currentItem.playbookRn === existingPlaybookRn);
        const currentStepsByRn = new Map(
            currentPlaybook.steps
                .filter((step) => step.stepRn)
                .map((step) => [step.stepRn, step]),
        );
        const nextSteps = playbookPayload.steps;
        const nextStepRns = new Set(nextSteps.filter((step) => step.stepRn).map((step) => step.stepRn));

        await backend.playbook.update(existingPlaybookRn, {
          title: playbookPayload.title,
          description: playbookPayload.description,
          groupName: playbookPayload.groupName,
        });

        for (const step of nextSteps) {
          const stepBody = {
            prompt: step.prompt,
            day: step.day,
            ownerRns: step.ownerRns || [],
          };

          if (!step.stepRn) {
            await backend.playbook.step.add(existingPlaybookRn, stepBody);
            continue;
          }

          const currentStep = currentStepsByRn.get(step.stepRn);

          if (currentStep && !areStepsEqual(currentStep, step)) {
            await backend.playbook.step.update(existingPlaybookRn, step.stepRn, stepBody);
          }
        }

        for (const currentStep of currentStepsByRn.values()) {
          if (!nextStepRns.has(currentStep.stepRn)) {
            await backend.playbook.step.remove(existingPlaybookRn, currentStep.stepRn);
          }
        }

        playbookResponse = await backend.playbook.get(existingPlaybookRn);
      }

      const storedPlaybook = toStoredPlaybook(playbookResponse);

      playbooksAdd(storedPlaybook);
      setSelectedPlaybookRn(storedPlaybook.playbookRn);

      closeNew();
    });
  }, [areStepsEqual, closeNew, playbookList, playbooksAdd, progressWrap, toStoredPlaybook]);

  React.useEffect(() => {
    if (selectedPlaybookRn === null) {
      return;
    }

    const hasSelectedPlaybook = playbookList.some((playbook) => playbook.playbookRn === selectedPlaybookRn);

    if (!hasSelectedPlaybook) {
      setSelectedPlaybookRn(null);
    }
  }, [playbookList, selectedPlaybookRn]);

  return (
    <>
      <Stack
        direction={{
          xs: 'column',
          md: 'row',
        }}
        alignItems='stretch'
        sx={{
          width: '100%',
          height: {
            md: 'calc(99vh - 32px)',
          },
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            alignSelf: 'stretch',
            display: 'flex',
          }}
        >
          <PlaybooksList
            groupedPlaybooks={groupedPlaybooks}
            openNew={openNew}
            onSelectPlaybook={handleSelectPlaybook}
            selectedPlaybookRn={selectedPlaybookRn}
          />
        </Box>
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            backgroundColor: '#FFFFFF',
          }}
        >
          <PlaybooksView
            playbook={selectedPlaybook}
            onDelete={() => setDeleteDialogOpen(true)}
            onEdit={handleEditPlaybook}
          />
        </Box>
      </Stack>
      {
        deleteDialogOpen &&
        <PlaybookDeleteDialog
          onClose={() => setDeleteDialogOpen(false)}
          onConfirm={handleDeletePlaybook}
        />
      }
      <PlaybookNew
        active={activeNew}
        onClose={closeNew}
        handleCreatePlaybook={handleCreatePlaybook}
        groupOptions={groupOptions}
        playbook={editingPlaybook}
      />
    </>
  );
};

PlaybooksScene.propTypes = {
  playbooks: PropTypes.array.isRequired,
  statusProgressWrap: PropTypes.func.isRequired,
  playbooksAdd: PropTypes.func.isRequired,
  playbooksLoadData: PropTypes.func.isRequired,
  playbooksRemove: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  playbooks: state.playbooks.allPlaybooks,
});

export default connect(mapStateToProps, dispatch)(React.memo(PlaybooksScene));
